/**
 * Theme Context Provider
 * Manages dynamic theming based on workplace settings
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useWorkplaceDetails } from '@/hooks/business/useWorkplaceDetails';
import { useAuth } from '@/hooks/useAuth';

interface ThemeContextType {
  themeColor: string;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, profile, currentCompany } = useAuth();
  const hasCompanyAssociation = !!(user && profile && currentCompany);
  const { data: workplaceDetails, isLoading } = useWorkplaceDetails({ enabled: hasCompanyAssociation });
  const themeColor = workplaceDetails?.theme_color || '#3B82F6'; // Default blue

  useEffect(() => {
    // Apply theme color to CSS custom properties
    // Skip accent overrides in dark mode to preserve the curated dark palette
    const isDark = document.documentElement.classList.contains('dark');
    if (themeColor && workplaceDetails && !isDark) {
      const root = document.documentElement;
      
      // Convert hex to HSL for CSS custom properties
      const hexToHsl = (hex: string) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        let s = 0;
        const l = (max + min) / 2;
        
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          
          switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
          }
          h /= 6;
        }
        
        return {
          h: Math.round(h * 360),
          s: Math.round(s * 100),
          l: Math.round(l * 100),
        };
      };
      
      const hsl = hexToHsl(themeColor);
      const hslString = `${hsl.h} ${hsl.s}% ${hsl.l}%`;
      
      // Update CSS custom properties for theming
      root.style.setProperty('--primary', hslString);
      
      // Generate complementary colors
      const lighterL = Math.min(98, hsl.l + 15);
      
      root.style.setProperty('--primary-foreground', `${hsl.h} ${Math.min(40, hsl.s)}% ${lighterL}%`);
      
      // Update sidebar theming
      const sidebarHsl = `${hsl.h} ${Math.round(hsl.s * 0.8)}% ${Math.round(hsl.l * 0.9)}%`;
      root.style.setProperty('--sidebar-primary', sidebarHsl);
      
      // Ring color for focus states
      root.style.setProperty('--ring', hslString);
    } else if (isDark) {
      // In dark mode, clear any previously set inline styles so CSS variables from .dark apply
      const root = document.documentElement;
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-foreground');
      root.style.removeProperty('--sidebar-primary');
      root.style.removeProperty('--ring');
    }
  }, [themeColor, workplaceDetails]);

  const value: ThemeContextType = {
    themeColor,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;